import { useEffect } from 'react'
import { Fade } from 'react-awesome-reveal'
import { useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Tooltip } from 'react-tooltip'

import { Button, ButtonHierarchy } from '../../Button'
import styles from './sticky-buttons.module.scss'
import { VehicleImage } from '@/components/styleguide/VehicleImage'
import { constants } from '@/lib/constants'
import { selectPaintOptions } from '@/store/selectors/vehicle'
import { Size, Theme } from '@/types/system'
import { Vehicle } from '@/types/vehicle'

type StickyButtonsProps = {
  imageStatus: string
  setImageStatus: (value: string) => void
  setActiveSection: (value: string) => void
  openSelector: string
  activeSection: string
  vehicle: Vehicle
}

export const StickyButtons = ({
  imageStatus,
  setImageStatus,
  openSelector,
  setActiveSection,
  activeSection,
  vehicle,
}: StickyButtonsProps) => {
  const isMobile = useMediaQuery({ maxWidth: 991 })
  const paintOptions = useSelector(selectPaintOptions)

  const onClickColourOptions = () => {
    setActiveSection(constants.configPageSections.COLOUR_OPTIONS)
    setImageStatus(constants.imageStatus.EXTERIOR)
  }

  const onClickExteriorOption = () => {
    setActiveSection(constants.configPageSections.OVERVIEW)
    setImageStatus(constants.imageStatus.EXTERIOR)
  }

  const onClickInteriorOption = () => {
    setActiveSection(constants.configPageSections.OVERVIEW)
    setImageStatus(constants.imageStatus.INTERIOR)
  }

  useEffect(() => {
    if (!isMobile && imageStatus === constants.imageStatus.INTERIOR_FULL_SCREEN) {
      setImageStatus(constants.imageStatus.INTERIOR)
    }
  }, [imageStatus, isMobile, setImageStatus])

  return (
    <div
      data-image-status={imageStatus}
      data-is-mobile={isMobile}
      className={`config-page__sticky-button ${styles['sticky-buttons']} ${imageStatus === constants.imageStatus.INTERIOR_FULL_SCREEN && isMobile ? ' config-page__sticky-button--right' : ''}`}
      data-is-selector-open={openSelector === '' ? 'false' : 'true'}>
      {!isMobile && (
        <Tooltip
          place="right"
          className={styles.tooltip}
          classNameArrow={styles['tooltip-arrow']}
          id="sticky-buttons-tooltip"
        />
      )}
      <Fade
        className={styles.left}
        triggerOnce
        delay={300}>
        {!isMobile && activeSection === constants.configPageSections.COLOUR_OPTIONS && (
          <div
            data-tooltip-id="sticky-buttons-tooltip"
            data-tooltip-content="Back to config"
            key="back-to-config">
            <Button
              onClick={() => setActiveSection(constants.configPageSections.OVERVIEW)}
              label=""
              icon="X"
              size={Size.Large}
              theme={Theme.dark}
              hierarchy={ButtonHierarchy.Filled}
              id="back-to-config"
            />
          </div>
        )}
        {imageStatus === constants.imageStatus.INTERIOR && (
          <button
            type="button"
            className={styles.sideview}
            onClick={() => setImageStatus(constants.imageStatus.EXTERIOR)}>
            <VehicleImage
              alt=""
              className={styles.thumbnail}
              height={120}
              width={172}
              imagePath={vehicle?.gallery?.images.filter(i => i.angle === 'sideview')?.[0].path}
              backupImagePath=""
              type="DEFAULT"
            />
          </button>
        )}
        {imageStatus === constants.imageStatus.EXTERIOR &&
          activeSection === constants.configPageSections.OVERVIEW && (
            <div
              data-tooltip-id="sticky-buttons-tooltip"
              data-tooltip-content="Colour options"
              key="color-options">
              {paintOptions.length > 0 && (
                <Button
                  onClick={onClickColourOptions}
                  isSquare
                  hasIcon
                  icon="Palette"
                  size={Size.Large}
                  theme={Theme.dark}
                  hierarchy={ButtonHierarchy.Filled}
                  id="colour-options"
                />
              )}
            </div>
          )}
      </Fade>
      <Fade
        className={styles.right}
        triggerOnce
        delay={300}>
        {imageStatus === constants.imageStatus.EXTERIOR &&
          activeSection !== constants.configPageSections.SPECIFICATION && (
            <button
              className={styles.dashboard}
              type="button"
              onClick={onClickInteriorOption}>
              <VehicleImage
                alt=""
                className={styles.thumbnail}
                height={120}
                width={172}
                imagePath={vehicle?.gallery?.images.filter(i => i.angle === 'dashboard')?.[0].path}
                backupImagePath=""
                type="GALLERY"
              />
            </button>
          )}
        {imageStatus === constants.imageStatus.INTERIOR && isMobile && (
          <Button
            onClick={() => setImageStatus(constants.imageStatus.INTERIOR_FULL_SCREEN)}
            isSquare
            icon="Scan"
            size={Size.Large}
            theme={Theme.dark}
            hierarchy={ButtonHierarchy.Filled}
          />
        )}
        {imageStatus === constants.imageStatus.INTERIOR_FULL_SCREEN && isMobile && (
          <Button
            onClick={onClickExteriorOption}
            isSquare
            icon="X"
            size={Size.Large}
            theme={Theme.dark}
            hierarchy={ButtonHierarchy.Filled}
            className={styles['position-right']}
          />
        )}
      </Fade>
    </div>
  )
}
